import React, { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/joy'
import { ArrowLeft as ArrowBack } from '@phosphor-icons/react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { DocumentForm } from '../components/billing'
import { invoicesApi, taxRatesApi } from '../lib/billingApi'
import { apiClient } from '../lib/api'
import type { TaxRate } from '../types/billing'

export default function InvoiceEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch invoice data
  const { data: invoice, isLoading: invoiceLoading, error: invoiceError } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(parseInt(id!)),
    enabled: !!id
  })

  // Fetch customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: async () => {
      const response = await apiClient.getContacts({ page_size: 1000 })
      return response.data
    }
  })

  // Fetch jobs (include completed — invoices are often tied after job close-out)
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['jobs', { includeCompleted: true, pageSize: 1000 }],
    queryFn: async () => {
      const response = await apiClient.getJobs(undefined, true, 1000)
      return response.data
    }
  })

  // Fetch tax rates
  const { data: taxRates = [], isLoading: taxRatesLoading } = useQuery<TaxRate[]>({
    queryKey: ['taxRates'],
    queryFn: async () => {
      const response = await taxRatesApi.list()
      return response.results
    }
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: any) => invoicesApi.update(parseInt(id!), data),
    onSuccess: () => {
      toast.success('Invoice updated successfully')
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      navigate(`/operations/billing/invoices/${id}`)
    },
    onError: (error: any) => {
      const data = error.response?.data
      const detail =
        data?.detail ||
        (Array.isArray(data?.non_field_errors) ? data.non_field_errors.join(' ') : null) ||
        (typeof data === 'object' && data
          ? Object.values(data).flat().join(' ')
          : null) ||
        'Failed to update invoice'
      toast.error(detail)
      setIsSubmitting(false)
    }
  })

  const invoiceStatus = (invoice as { status?: string } | undefined)?.status
  const isPaid = invoiceStatus === 'paid'

  const handleSubmit = async (data: any) => {
    setIsSubmitting(true)

    // Paid invoices: metadata-only PATCH (job/notes/terms/dates). Never send
    // customer, amounts, or line items — backend rejects those on paid docs.
    const updateData = isPaid
      ? {
          job: data.job || null,
          document_date: data.document_date,
          due_date: data.due_date || null,
          payment_terms: data.payment_terms,
          custom_payment_terms: data.custom_payment_terms || '',
          terms_and_conditions: data.terms_and_conditions || '',
          notes: data.notes || '',
          internal_notes: data.internal_notes || '',
        }
      : {
          customer: data.customer,
          job: data.job || null,
          document_date: data.document_date,
          due_date: data.due_date || null,
          payment_terms: data.payment_terms,
          custom_payment_terms: data.custom_payment_terms || '',
          terms_and_conditions: data.terms_and_conditions || '',
          notes: data.notes || '',
          internal_notes: data.internal_notes || '',
          is_recurring: data.is_recurring || false,
          recurring_frequency: data.recurring_frequency || null,
          late_fee_percentage: data.late_fee_percentage || 0,
          discount_amount: data.discount_amount || 0,
          include_overhead: data.include_overhead || false,
          overhead_percentage: data.overhead_percentage || null,
          include_profit: data.include_profit || false,
          profit_percentage: data.profit_percentage || null,
          line_items: data.line_items,
        }

    try {
      await updateMutation.mutateAsync(updateData)
    } catch (error) {
      throw error // Keep the form draft and show server validation fields.
    }
  }

  const handleCancel = () => {
    navigate(`/operations/billing/invoices/${id}`)
  }

  const isLoading = invoiceLoading || customersLoading || jobsLoading || taxRatesLoading

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (invoiceError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="danger">
          Failed to load invoice: {invoiceError.message}
        </Alert>
        <Button
          component={Link}
          to="../.."
          startDecorator={<ArrowBack />}
          variant="plain"
          sx={{ mt: 2 }}
        >
          Back to Invoices
        </Button>
      </Box>
    )
  }

  if (!invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">
          Invoice not found
        </Alert>
        <Button
          component={Link}
          to="../.."
          startDecorator={<ArrowBack />}
          variant="plain"
          sx={{ mt: 2 }}
        >
          Back to Invoices
        </Button>
      </Box>
    )
  }

  // Void/cancelled stay fully locked; paid allows metadata-only edits.
  if (invoiceStatus === 'void' || invoiceStatus === 'cancelled') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">
          This invoice cannot be edited because it has been {invoiceStatus}.
        </Alert>
        <Button
          component={Link}
          to=".."
          startDecorator={<ArrowBack />}
          variant="plain"
          sx={{ mt: 2 }}
        >
          Back to Invoice
        </Button>
      </Box>
    )
  }

  // customersData and jobsData are already the results arrays due to ApiClient extracting them
  const customers = customersData || []
  const jobs = jobsData || []

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          component={Link}
          to=".."
          relative="path"
          startDecorator={<ArrowBack />}
          variant="plain"
          size="sm"
          sx={{ mb: 2 }}
        >
          Back to Invoice
        </Button>
        
        <Typography level="h1">
          Edit Invoice {(invoice as any).document_number}
        </Typography>
      </Box>
      
      <DocumentForm
        document={invoice as any}
        documentType="invoice"
        customers={customers}
        jobs={jobs}
        taxRates={taxRates}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isSubmitting}
        lockFinancialFields={isPaid}
      />
    </Box>
  )
}
